import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import dayjs from "dayjs";

import WPDatePicker from "../../../packages/WPDatePicker/WPDatePicker";
import WPDropdown from "../../../packages/WPDropdown";
import WPHeading from "../../../packages/WPHeading";
import WPInput from "../../../packages/WPInput";

import useTimezone from "../../../hooks/useTimezone";

import {
  DATE_FORMATS,
  STREAM_NAME_MAX_LENGTH,
} from "../../../constants/global.constants";
import {
  HIDDEN_LICENSE_IDS,
  LICENSE_STATUS,
} from "../../../constants/LicenseManagement.constants";

import { CalendarIcon, IdCardIcon } from "../../../assets/icons";

import "./AssignLicenseFields.scss";

const AssignLicenseFields = ({
  licenseForm,
  setLicenseForm,
  userData,
  isAssignLicense,
  isRenewLicense,
  dataTestId,
}) => {
  const { t } = useTranslation();
  const { formatTime } = useTimezone();
  const startDate = dayjs();

  const licensePermissions = useSelector((state) => state.licensePermissions);

  const {
    selectedLicenseType,
    selectedLicenseName,
    expiryDate,
    keyAccountManager,
  } = licenseForm;

  const licenseTypeOptions = useMemo(() => {
    if (!Array.isArray(licensePermissions)) return [];
    const uniqueTypes = Array.from(
      new Set(licensePermissions.map((item) => item.licenseType)),
    );
    return uniqueTypes.map((type) => ({
      label: type,
      value: type,
    }));
  }, [licensePermissions]);

  /**
   * Generates dropdown options for the user's licenses, combining license type, name, and formatted expiry date.
   * Each option contains a label object (with nameType and expiry) and the full license object as value.
   * In terminate mode (not assign or renew), filters out licenses with licenseStatus: "Expired" , "Terminated"
   * so only active/expiring licenses are shown for termination.
   */
  const userLicenseOptions = useMemo(() => {
    let licenses = userData?.licenses || [];
    if (!licenses.length) return [];
    if (!isAssignLicense && !isRenewLicense) {
      licenses = licenses.filter(
        (lic) =>
          lic.licenseStatus !== LICENSE_STATUS.expired &&
          lic.licenseStatus !== LICENSE_STATUS.terminated,
      );
    }
    licenses = licenses.filter((lic) => !HIDDEN_LICENSE_IDS.includes(lic.id));
    return licenses.map(
      ({ licenseType = "", name = "", expiryDate, ...rest }) => ({
        label: {
          nameType: `${licenseType} ${name}`,
          expiry: expiryDate
            ? formatTime(expiryDate, true, DATE_FORMATS.MMM_DD_YYYY)
            : "",
        },
        value: { licenseType, name, expiryDate, ...rest },
      }),
    );
  }, [userData, isAssignLicense, isRenewLicense, formatTime]);

  /**
   * Generates the license name dropdown options based on selected license type.
   * If threeDotMenuAssignLicense is true, filters out licenses already assigned to the user.
   * Excludes 'Public Default' licenses from the options.
   */
  const isSingleUserMode = !!userData;
  const licenseNameOptions = useMemo(() => {
    if (!selectedLicenseType) return [];
    let availableLicenses = licensePermissions;

    if (isSingleUserMode && userData?.licenses) {
      const assignedIdsSet = new Set(
        userData.licenses.map((license) => license.id),
      );
      availableLicenses = licensePermissions.filter(
        ({ id }) => !assignedIdsSet.has(id),
      );
    }

    return availableLicenses.reduce((acc, licenseItem) => {
      if (
        licenseItem.licenseType === selectedLicenseType.value &&
        !HIDDEN_LICENSE_IDS.includes(licenseItem.id)
      ) {
        acc.push({ label: licenseItem.name, value: licenseItem.id });
      }
      return acc;
    }, []);
  }, [licensePermissions, selectedLicenseType, isSingleUserMode, userData]);

  /**
   * Updates the selectedLicenseName in the form state based on the provided options array.
   * - If no license is selected and options are available, selects the first option.
   * - If a license is selected but options are empty, clears the selection.
   * Used to keep the license selection in sync with available options.
   * @param {Array} options - The array of license options to consider for selection.
   */
  const updateLicenseName = (options) => {
    if (!selectedLicenseName && options.length > 0) {
      setLicenseForm((prev) => ({ ...prev, selectedLicenseName: options[0] }));
    } else if (selectedLicenseName && options.length === 0) {
      setLicenseForm((prev) => ({ ...prev, selectedLicenseName: null }));
    }
  };

  useEffect(() => {
    if (isAssignLicense) {
      if (!selectedLicenseType && licenseTypeOptions.length > 0) {
        setLicenseForm((prev) => ({
          ...prev,
          selectedLicenseType: licenseTypeOptions[0],
        }));
      }
      updateLicenseName(licenseNameOptions);
    } else {
      updateLicenseName(userLicenseOptions);
    }
  }, [
    licenseTypeOptions,
    licenseNameOptions,
    userLicenseOptions,
    selectedLicenseType,
    selectedLicenseName,
    setLicenseForm,
    isAssignLicense,
  ]);

  const licenseLabel = (option) => option.label;

  const handleLicenseTypeChange = (option) => {
    setLicenseForm((prev) => ({
      ...prev,
      selectedLicenseType: option,
      isDataChanged: true,
      emailInputError: "",
      clearUserError: !prev.clearUserError,
      selectedLicenseName: null,
    }));
  };

  const handleLicenseNameChange = (option) => {
    const keyManager = isAssignLicense
      ? ""
      : option?.value?.licenseApprover || "";
    setLicenseForm((prev) => ({
      ...prev,
      selectedLicenseName: option,
      keyAccountManager: keyManager,
      isDataChanged: true,
      emailInputError: "",
      clearUserError: !prev.clearUserError,
    }));
  };

  const handleExpiryDateChange = (value) => {
    setLicenseForm((prev) => ({
      ...prev,
      expiryDate: value,
      isDataChanged: true,
    }));
  };

  const handleKeyManager = (event) => {
    setLicenseForm((prev) => ({
      ...prev,
      keyAccountManager: event.target.value,
      isDataChanged: true,
    }));
  };

  const validateKeyManager = (value) => {
    if (!value) return null;
    if (value.length >= STREAM_NAME_MAX_LENGTH) {
      return {
        warning: t("maxLengthExceeded", { maxLength: STREAM_NAME_MAX_LENGTH }),
      };
    }
    return null;
  };

  const disableBeforeStartDate = (current) =>
    current && current.isBefore(startDate.add(1, "day"), "day");

  /**
   * Renders a custom dropdown option for licenses, displaying the license name/type and expiry date with an icon.
   * @param {Object} props
   * @param {string} props.nameType - Combined license type and name
   * @param {string} props.expiry - License expiry date (formatted)
   */
  const LicenseDropdownOption = ({ nameType, expiry }) => (
    <div className="license-dropdown-option">
      <span
        className="license-dropdown-option__name"
        id="license_dropdown_name"
      >
        <IdCardIcon color="var(--du-color-black_70)" />
        {nameType}
      </span>
      <WPHeading
        text={expiry}
        size="xxSmall"
        color="var(--du-color-black_70)"
        fontNoto
        secondary
        id="license_dropdown_expiry"
      />
    </div>
  );

  const getLicenseDropdownOptionLabel = (option) => (
    <LicenseDropdownOption
      nameType={option.label.nameType}
      expiry={option.label.expiry}
    />
  );

  /**
   * Filters license dropdown options based on user input, matching against license name/type and expiry date.
   */
  const filterLicenseDropdownOption = (option, input) => {
    const { nameType = "", expiry = "" } = option.data.label || {};
    const search = input.trim().toLowerCase();
    return [nameType, expiry].some((field) =>
      field.toLowerCase().includes(search),
    );
  };

  const modalHeadingText = useMemo(() => {
    if (isAssignLicense) return t("licenseManagement.selectLicense");
    if (isRenewLicense) return t("licenseManagement.renewLicenseHeader");
    return t("licenseManagement.terminateLicenseHeader");
  }, [isAssignLicense, isRenewLicense, t]);

  const licenseDateHeadingText = useMemo(
    () =>
      isAssignLicense
        ? `${t("licenseManagement.startDate")}  : ${startDate.format(DATE_FORMATS.MMM_DD_YYYY)} (${t("licenseManagement.today")})`
        : `${t("licenseManagement.assignedDate")} : ${formatTime(selectedLicenseName?.value?.assignedDate, true, DATE_FORMATS.MMM_DD_YYYY)}`,
    [isAssignLicense, startDate, selectedLicenseName, t, formatTime],
  );

  return (
    <>
      <div className="assign-license-header">
        <WPHeading
          text={modalHeadingText}
          type="default"
          className="modal-heading"
          fontNoto
        />
        <div className="license-start-date">
          <CalendarIcon fill="var(--du-color-black)" />
          <WPHeading
            text={licenseDateHeadingText}
            className="license-assign-date"
            type="default"
            fontNoto
            secondary
            size="xSmall"
            id={dataTestId}
          />
        </div>
      </div>
      <div className="license-dropdowns">
        {isAssignLicense ? (
          <>
            <WPDropdown
              id="license-type-dropdown"
              label={t("licenseManagement.licenseType")}
              onChange={handleLicenseTypeChange}
              options={licenseTypeOptions}
              getOptionLabel={licenseLabel}
              value={selectedLicenseType}
              required
            />
            <WPDropdown
              id="license-name-dropdown"
              label={t("licenseManagement.licenseName")}
              onChange={handleLicenseNameChange}
              options={licenseNameOptions}
              getOptionLabel={licenseLabel}
              value={selectedLicenseName}
              required
            />
          </>
        ) : (
          <WPDropdown
            id="license-type-name-dropdown"
            label={t("licenseManagement.license")}
            onChange={handleLicenseNameChange}
            options={userLicenseOptions}
            getOptionLabel={getLicenseDropdownOptionLabel}
            filterOption={filterLicenseDropdownOption}
            value={selectedLicenseName}
            required
          />
        )}
      </div>
      {(isAssignLicense || isRenewLicense) && (
        <div className="license-assigned-date-inputs">
          <WPDatePicker
            label={t("licenseManagement.expirationDate")}
            value={expiryDate}
            onChange={handleExpiryDateChange}
            required
            disabledDate={disableBeforeStartDate}
            dataTestId="expiry_date_picker_input"
            helperText={t("licenseManagement.startDateHelperText")}
          />
          <WPInput
            className="key-account-manager-input"
            id="key-account-manager-input"
            label={t("licenseManagement.keyAccountManager")}
            value={keyAccountManager}
            onChange={handleKeyManager}
            maxLength={STREAM_NAME_MAX_LENGTH}
            validate={validateKeyManager}
          />
        </div>
      )}
    </>
  );
};

export default AssignLicenseFields;
